import { useJoinWaitlistMutation } from 'store/services/waitlist/waitlistApi';
import { JoinWaitlistTabProps } from './components/LoginForm/forms/JoinWaitlistTab/types';
import { ApiError } from 'store/types';
import { useLoginMutation } from 'store/services/auth/authApi';
import { LoginTabProps } from './components/LoginForm/forms/LoginTab/types';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { performLogin } from './performLogin';

export const useLogin = (): {
  loginTabProps: LoginTabProps;
  joinWaitlistTabProps: JoinWaitlistTabProps;
} => {
  const dispatch = useDispatch();
  const [joinWaitlist, joinWaitlistState] = useJoinWaitlistMutation();
  const [login, loginState] = useLoginMutation();
  const onLoginPress = async (email: string, password: string) => {
    try {
      // Cache resets, the mutation and the session dispatch live in performLogin,
      // shared with the onboarding password step (Asana 1219793876039315).
      // Note: AuthRoute handles redirect to returnTo or /home when user becomes authenticated
      // We don't navigate here to avoid race conditions with AuthRoute
      await performLogin(dispatch, login, email, password);
    } catch (e: any) {
      // Log error without sensitive details in production
      if (process.env.NODE_ENV === 'development') {
        console.error('Login failed - Status:', e.status);
      }
      
      // Show error toast directly here as well
      let errorMessage = 'Login failed. Please try again.';
      if (e.status === 401) {
        errorMessage = 'Invalid email or password. Please check your credentials and try again.';
      } else if (e.status === 404) {
        errorMessage = 'No account found with this email address. Please sign up first.';
      } else if (e.data?.message) {
        errorMessage = e.data.message;
      }
      
      toast.error(errorMessage);
      // Error will also be displayed via loginState.error in the form
    }
  };

  return {
    loginTabProps: {
      ...loginState,
      error: loginState.error as ApiError,
      login: onLoginPress,
    },
    joinWaitlistTabProps: {
      ...joinWaitlistState,
      error: joinWaitlistState.error as ApiError,
      joinWaitlist: async (email: string) => {
        try {
          await joinWaitlist({ email }).unwrap();
          toast.success('Successfully joined the waitlist! We\'ll notify you when space is available.');
        } catch (error: any) {
          toast.error(error.data?.message || 'Failed to join waitlist. Please try again.');
        }
      },
    },
  };
};
